import { Close } from "@lazarentertainment/web-design-system/components/iconOutline"
import {
  Overlay,
  OverlayProps,
} from "@lazarentertainment/web-design-system/components/overlay"
import * as Dialog from "@radix-ui/react-dialog"
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
} from "react"

import { Button } from "./Button"

export const DialogRoot = Dialog.Root
export const DialogTrigger = Dialog.Trigger
export const DialogPortal = Dialog.Portal
export type DialogOverlayProps = OverlayProps

/**
 * This intentionally exposes the same interface as the base <Overlay /> element
 * without any of the Radix properties. This should be useful for almost if not all
 * cases. In case customization is required, compose the elements for that unique
 * case.
 */
export const DialogOverlay = forwardRef<
  HTMLDivElement,
  DialogOverlayProps
>((props, ref) => {
  return (
    <Dialog.Overlay asChild>
      <Overlay ref={ref} {...props} />
    </Dialog.Overlay>
  )
})

DialogOverlay.displayName = "DialogOverlay"

export const dialogSizes = ["md", "lg", "xl"] as const

export type DialogSize = (typeof dialogSizes)[number]

export type DialogContentProps = ComponentPropsWithoutRef<
  typeof Dialog.Content
> & {
  /**
   * Indicates the maximum size of the Dialog on medium and large screens.
   * Small screens (below 960px) will not respect this property.
   */
  "data-size"?: DialogSize
}

export const DialogContent = forwardRef<
  HTMLDivElement,
  DialogContentProps
>(({ children, className, ...props }, ref) => {
  return (
    <Dialog.Content
      className={`dialog-content ${className ?? ""}`}
      ref={ref}
      {...props}
    >
      {children}
    </Dialog.Content>
  )
})

DialogContent.displayName = "DialogContent"

export type DialogTitleProps = ComponentPropsWithoutRef<
  typeof Dialog.Title
>

export const DialogTitle = forwardRef<
  HTMLHeadingElement,
  DialogTitleProps
>(({ className, ...props }, ref) => {
  return (
    <Dialog.Title
      className={`title-md text-color-primary-subdued ${className ?? ""}`}
      {...props}
      ref={ref}
    />
  )
})

DialogTitle.displayName = "DialogTitle"

type DialogCloseButtonProps = Omit<
  ComponentPropsWithoutRef<typeof Dialog.Close>,
  "asChild"
>

export const DialogCloseButton = forwardRef<
  HTMLButtonElement,
  DialogCloseButtonProps
>(({ ...props }, ref) => {
  return (
    <Dialog.Close asChild {...props} ref={ref}>
      <Button
        iconOnly
        data-variant="tertiary-gray"
        aria-label="Close"
        icon={Close}
      />
    </Dialog.Close>
  )
})

DialogCloseButton.displayName = "DialogCloseButton"

export type DialogHeaderProps = ComponentPropsWithoutRef<"div"> & {
  title?: string
  description?: string
  topLeftContent?: ReactNode
  topRightContent?: ReactNode
}

export const DialogHeader = forwardRef<HTMLDivElement, DialogHeaderProps>(
  (
    { title, description, topLeftContent, topRightContent, ...props },
    ref,
  ) => {
    return (
      <div className="dialog-header" {...props} ref={ref}>
        <div className="dialog-top-left-content">{topLeftContent}</div>
        {(title || description) && (
          <div className="dialog-title-section">
            {title && (
              <Dialog.Title className="dialog-title title-lg text-color-primary">
                {title}
              </Dialog.Title>
            )}
            {description && (
              <Dialog.Description className="dialog-description body-md text-color-secondary-subdued">
                {description}
              </Dialog.Description>
            )}
          </div>
        )}
        <div className="dialog-top-right-content">{topRightContent}</div>
      </div>
    )
  },
)

DialogHeader.displayName = "DialogHeader"

export type DialogBodyProps = ComponentPropsWithoutRef<"div">

export const DialogBody = forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ ...props }, ref) => {
    return <div className="dialog-body" {...props} ref={ref} />
  },
)

DialogBody.displayName = "DialogBody"
